import React, { useEffect } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"

import classNames from "classnames"

import { useHydratedFormValues } from "shared/lib/form/useHydratedFormValues"
import { noop } from "shared/lib/noop"
import { SelectOption } from "shared/types/Select"
import { WithChildren } from "shared/types/WithChildren"
import { WithClassName } from "shared/types/WithClassName"

import { ProductFilterFormData } from "entities/products/types/ProductFilter"

import { ProductFilterBuildingTypeInput } from "./ui/ProductFilterBuildingTypeInput"
import { ProductFilterProductGroupInput } from "./ui/ProductFilterProductGroupInput"
import { ProductFilterRoomTypeInput } from "./ui/ProductFilterRoomTypeInput"
import { ProductFilterSearchInput } from "./ui/ProductFilterSearchInput"
import { ProductFilterChipList } from "./ui/product-filter-chip-list/ProductFilterChipList"

export interface PortfolioFilterModel {
  defaultValues?: ProductFilterFormData
  onChange?: (filterState: ProductFilterFormData) => void
}

export interface PortfolioFilterProps
  extends PortfolioFilterModel,
    WithClassName,
    WithChildren {}

const ProductFilterRoot = React.memo(
  ({ defaultValues, onChange, className, children }: PortfolioFilterProps) => {
    const form = useForm<ProductFilterFormData>({ defaultValues })

    useHydratedFormValues(form, defaultValues)

    useEffect(() => {
      const unsubscribe = form.subscribe({
        formState: { values: true },
        callback({ values }) {
          onChange?.(values)
        }
      })
      return () => unsubscribe()
    }, [form])

    return (
      <FormProvider {...form}>
        <form
          className={classNames("product-filter", className)}
          onSubmit={form.handleSubmit(noop)}
        >
          {children}
        </form>
      </FormProvider>
    )
  }
)

const ChipList = React.memo(
  ({
    productGroupOptions
  }: {
    productGroupOptions: SelectOption<string>[] | undefined
  }) => {
    const { watch, reset } = useFormContext<ProductFilterFormData>()

    const formData = watch()

    return (
      <ProductFilterChipList
        filterState={formData}
        onChange={reset}
        productGroupOptions={productGroupOptions}
      />
    )
  }
)

export const ProductFilter = {
  Root: ProductFilterRoot,
  Search: ProductFilterSearchInput,
  ProductGroup: ProductFilterProductGroupInput,
  BuildingType: ProductFilterBuildingTypeInput,
  RoomType: ProductFilterRoomTypeInput,
  ChipList
}
